import { Injectable } from '@angular/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

/**
 * Fotos y GPS vía Capacitor en nativo; en PWA/navegador, Camera usa PWA Elements si están registrados
 * (defineCustomElements en main.ts). Ver https://capacitorjs.com/docs/web/pwa-elements
 * e Ionic Native: https://ionicframework.com/docs/native
 */
@Injectable({
  providedIn: 'root'
})
export class CameraGeolocationService {
  constructor() {}

  /**
   * Toma una foto usando la cámara o selecciona de la galería
   */
  /**
   * Imagen como `File` para adjuntos (p. ej. registro de visita offline en Capacitor).
   * Usa URI + `fetch` para evitar cargar todo en Base64 en memoria.
   */
  async obtenerFotoComoArchivo(source: 'camera' | 'gallery'): Promise<File> {
    await Camera.requestPermissions();
    const image = await Camera.getPhoto({
      quality: 82,
      allowEditing: false,
      resultType: CameraResultType.Uri,
      source: source === 'camera' ? CameraSource.Camera : CameraSource.Photos,
    });
    const rawPath = image.webPath ?? (image as { path?: string }).path;
    if (!rawPath) {
      throw new Error('No se obtuvo ruta de la imagen.');
    }
    const url = Capacitor.convertFileSrc(rawPath);
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error('No se pudo leer la imagen seleccionada.');
    }
    const blob = await res.blob();
    const fmt = (image.format || 'jpeg').toLowerCase();
    const ext = fmt === 'jpeg' || fmt === 'jpg' ? 'jpg' : fmt === 'png' ? 'png' : fmt === 'webp' ? 'webp' : 'jpg';
    const mime =
      blob.type && blob.type.startsWith('image/')
        ? blob.type
        : ext === 'png'
          ? 'image/png'
          : ext === 'webp'
            ? 'image/webp'
            : 'image/jpeg';
    const name = `visita-registro-${Date.now()}.${ext}`;
    return new File([blob], name, { type: mime });
  }

  async tomarFoto(source: 'camera' | 'gallery' = 'camera'): Promise<string> {
    try {
      const image = await Camera.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: source === 'camera' ? CameraSource.Camera : CameraSource.Photos
      });

      // Retornar la imagen en formato base64
      return `data:image/${image.format};base64,${image.base64String}`;
    } catch (error) {
      console.error('Error al tomar foto:', error);
      throw error;
    }
  }

  /**
   * Obtiene la ubicación actual del dispositivo usando Capacitor Geolocation
   * Funciona offline en dispositivos móviles usando GPS nativo
   */
  async obtenerUbicacion(): Promise<{ latitud: number; longitud: number } | null> {
    try {
      // Verificar permisos primero
      const permissions = await Geolocation.checkPermissions();
      
      if (permissions.location !== 'granted') {
        // Solicitar permisos si no están otorgados
        const request = await Geolocation.requestPermissions();
        if (request.location !== 'granted') {
          console.warn('Permisos de geolocalización denegados');
          return null;
        }
      }

      // Obtener ubicación usando Capacitor Geolocation
      // enableHighAccuracy: true fuerza el uso de GPS (funciona offline)
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      });

      return {
        latitud: position.coords.latitude,
        longitud: position.coords.longitude
      };
    } catch (error) {
      console.error('Error al obtener ubicación:', error);
      // Si Capacitor falla, intentar con la API del navegador como fallback
      if (Capacitor.isNativePlatform()) {
        // En plataforma nativa, si Capacitor falla, no hay fallback
        return null;
      } else {
        // En web, usar la API del navegador como fallback
        return this.obtenerUbicacionNavegador();
      }
    }
  }

  /**
   * Fallback: Obtiene la ubicación usando la API del navegador
   * Solo se usa en web cuando Capacitor no está disponible
   */
  private obtenerUbicacionNavegador(): Promise<{ latitud: number; longitud: number } | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.warn('Geolocalización no está disponible');
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitud: position.coords.latitude,
            longitud: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error al obtener ubicación del navegador:', error);
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  }

  /**
   * Verifica si la cámara está disponible
   */
  async verificarDisponibilidadCamara(): Promise<boolean> {
    try {
      if (Capacitor.isNativePlatform()) {
        // En plataforma nativa, verificar permisos
        const permissions = await Camera.checkPermissions();
        return permissions.camera === 'granted';
      } else {
        // En web, verificar si getUserMedia está disponible
        return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
      }
    } catch (error) {
      console.error('Error al verificar disponibilidad de cámara:', error);
      return false;
    }
  }

  /**
   * Solicita permisos de cámara
   */
  async solicitarPermisosCamara(): Promise<boolean> {
    try {
      if (Capacitor.isNativePlatform()) {
        const permissions = await Camera.requestPermissions();
        return permissions.camera === 'granted';
      } else {
        // En web, intentar acceder a la cámara
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          stream.getTracks().forEach(track => track.stop());
          return true;
        } catch {
          return false;
        }
      }
    } catch (error) {
      console.error('Error al solicitar permisos de cámara:', error);
      return false;
    }
  }
}





